import { setNativeAuthStatus } from '@store/reducers/commonStore';
import { getAuthority, isMacosPlatform } from '@utils/auth';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAppDispatch } from '../store/store';
import type { OidcUser } from '../types/oidcUser';
import { useExchangeToken } from './apiHooks/tokenValidationHooks';

const { VITE_COMPANION_CLIENT_ID } = import.meta.env;

/**
 * How long we wait for the native shell to call `window.setAuthTokens` before
 * giving up. Route guards show a spinner for the whole window, so this is the
 * difference between an error screen and an endless loader.
 */
export const NATIVE_AUTH_TIMEOUT_MS = 15000;

/**
 * Hook to handle authentication via native bridge from macOS app
 * macOS app calls: window.setAuthTokens({ accessToken, idToken?, platform })
 * or window.setAuthTokens({ oidcUser, platform })
 *
 * Progress is published to `commonStore.nativeAuthStatus` so route guards can
 * wait for the exchange instead of redirecting to the hosted login page.
 */
export const useNativeBridgeAuth = () => {
  const hasProcessed = useRef(false);
  const auth = useAuth();
  const dispatch = useAppDispatch();
  // const { mutateAsync: validateToken } = useValidateToken();
  const { mutateAsync: exchangeToken } = useExchangeToken();

  // helper to process tokens
  const processTokens = useCallback(
    async (accessToken: string, idToken: string | null) => {
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      // validate token format (basic check for JWT structure)
      if (!accessToken.includes('.') || accessToken.length < 20) {
        console.error('Invalid access token format');
        // allow a later injection to retry with a usable token
        hasProcessed.current = false;
        dispatch(setNativeAuthStatus('error'));
        return;
      }

      try {
        const { access_token, refresh_token } = await exchangeToken(
          accessToken,
        );

        if (access_token) {
          const userData = jwtDecode<OidcUser['profile']>(access_token);
          const nowInSeconds = Math.floor(Date.now() / 1000);
          const scope =
            userData.scope ||
            // cspell:disable-next-line
            'openid profile offline_access napsterai.companions.identity.main';

          const oidcUser = {
            access_token: access_token,
            id_token: idToken || undefined,
            token_type: 'Bearer',
            refresh_token,
            profile: {
              sub: userData.sub || '',
              email: userData.email || '',
              name: userData.name || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              organization: userData.organization || [],
              role: userData.role,
              // required IdTokenClaims properties
              iss: '',
              aud: '',
              exp: userData.exp || nowInSeconds + 3600,
              iat: nowInSeconds,
            },
            expires_at: userData.exp,
            scope,
            // required User properties with sensible defaults
            session_state: null,
            state: null,
            expires_in: userData.exp ? userData.exp - nowInSeconds : 3600,
            expired: false,
            scopes: scope,
            toStorageString: function () {
              return JSON.stringify(this);
            },
          } as unknown as OidcUser;

          // check both URL param and localStorage for platform
          const platform =
            window.location.search.includes('platform') ||
            localStorage.getItem('napster_platform') !== null;

          const LSKey = `oidc.user:${getAuthority()}:${
            VITE_COMPANION_CLIENT_ID as string
          }`;

          localStorage.setItem(LSKey, JSON.stringify(oidcUser));

          auth.events.load(oidcUser);
          dispatch(setNativeAuthStatus('success'));

          console.log(
            `✅ Authentication set via native bridge for platform: ${platform}${
              idToken ? ' (with id_token)' : ''
            }`,
          );

          // clean up native bridge data from window
          delete window.__NATIVE_AUTH__;
        } else {
          console.error('❌ Token exchange returned no access token');
          hasProcessed.current = false;
          dispatch(setNativeAuthStatus('error'));
        }
      } catch (error) {
        console.error('❌ Error processing token authentication:', error);
        hasProcessed.current = false;
        dispatch(setNativeAuthStatus('error'));
      }
    },
    [exchangeToken, auth.events, dispatch],
  );

  useEffect(() => {
    // only process once per component lifecycle
    if (hasProcessed.current) return;

    // helper to handle auth data
    const handleAuthData = (nativeData: NativeAuthConfig) => {
      if (nativeData.platform) {
        localStorage.setItem('napster_platform', nativeData.platform);
      }

      // extract access_token from either format
      const accessToken =
        nativeData.oidcUser?.access_token || nativeData.accessToken;
      const idToken = nativeData.oidcUser?.id_token || nativeData.idToken;

      if (!accessToken) {
        console.error('No access token provided');
        dispatch(setNativeAuthStatus('error'));
        return;
      }

      void processTokens(accessToken, idToken || null);
    };

    // check if auth data was already provided (before React loaded)
    if (window.__NATIVE_AUTH__) {
      handleAuthData(window.__NATIVE_AUTH__);
      return;
    }

    // listen for native bridge event (if called after React loads)
    const handleNativeAuth = (event: CustomEvent) => {
      if (!hasProcessed.current && event.detail) {
        handleAuthData(event.detail as NativeAuthConfig);
      }
    };

    window.addEventListener(
      'nativeAuthReady',
      handleNativeAuth as EventListener,
    );

    // on macOS the whole UI waits for this, so fail loudly instead of
    // spinning forever when the shell never calls setAuthTokens
    const timeoutId = isMacosPlatform()
      ? window.setTimeout(() => {
          if (!hasProcessed.current) {
            console.error('❌ Native bridge auth timed out');
            dispatch(setNativeAuthStatus('error'));
          }
        }, NATIVE_AUTH_TIMEOUT_MS)
      : undefined;

    return () => {
      window.removeEventListener(
        'nativeAuthReady',
        handleNativeAuth as EventListener,
      );
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [processTokens, dispatch]);
};
